import React from "react";
import { Icon, notify, confirmDialog } from "../registry";
import type { SiteContent, CityPage } from "./types";
import { Group } from "./fields";

/* ============================================================
   Website builder — town pages the contractor has EARNED
   (server/src/sites/earnedcities.js, migration #40).
   A town with 3 finished jobs, or 2 reviews that name it, gets its page
   drafted from their work there. Shown in the Service area tab:
     "Suggested: Howard, 4 jobs" → Preview · Add · Dismiss
   Add puts the page into the DRAFT (and switches off the town page it swaps
   out at the cap) like any other edit; the owner publishes as usual.
   ============================================================ */

type Suggestion = {
  id: string; town: string; jobs: number; reviews: number; photos: number; draft: CityPage | null;
  swap: { slug: string; name: string; jobs: number } | null; suggestedAt: string | null; dismissals: number;
};

/* ---------------- pure helpers (web/scripts/unit-sitegrowth2.mjs) ---------------- */

/* "Suggested: Howard, 4 jobs" / "…, 2 reviews" / "…, 4 jobs · 2 reviews" */
export function earnedTitle(s: { town: string; jobs: number; reviews: number }) {
  const bits = [s.jobs ? `${s.jobs} job${s.jobs === 1 ? "" : "s"}` : "", s.reviews ? `${s.reviews} review${s.reviews === 1 ? "" : "s"}` : ""].filter(Boolean);
  return `Suggested: ${s.town}${bits.length ? ", " + bits.join(" · ") : ""}`;
}

/* The draft with the earned page in it: an existing page for the town is
   replaced (it keeps its slug), else the page is added; the swapped page is
   switched off. Returns the new cities list. */
export function withEarnedCity(cities: CityPage[], page: CityPage, swapSlug: string | null) {
  const key = (s: string) => String(s || "").trim().toLowerCase();
  const at = cities.findIndex((c) => c.slug === page.slug || key(c.name) === key(page.name));
  const next = cities.map((c) => ({ ...c }));
  if (at >= 0) next[at] = { ...next[at], ...page, slug: next[at].slug, on: true };
  else next.push({ ...page, on: true });
  if (swapSlug) { const s = next.find((c) => c.slug === swapSlug && c.slug !== (at >= 0 ? next[at].slug : page.slug)); if (s) s.on = false; }
  return next;
}

const fmtShort = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; };

/* ---------------- the page, as the site will show it ---------------- */
function CityPreview({ c }: { c: CityPage }) {
  const photos = (c.photos || []).filter((p) => p && p.src);
  return (
    <div className="ws-sugg-preview">
      {photos.length > 0 && (
        <div className="ws-sugg-photos">
          {photos.map((ph, i) => <img key={i} src={ph.src} alt={ph.alt || ""} title={ph.alt || ""} loading="lazy" />)}
        </div>
      )}
      <div className="ws-sugg-body">
        <h4>Fence installation in {c.name}</h4>
        {c.tagline && <p><b>{c.tagline}</b></p>}
        {(c.intro || []).map((p, i) => <p key={i}>{p}</p>)}
        {c.why && <p className="ws-sugg-tip">{c.why}</p>}
        {(c.reviews || []).length > 0 && (
          <div className="ws-sugg-faqs">
            <h5>What {c.name} customers say</h5>
            {(c.reviews || []).map((r, i) => <p key={i}>“{r.text}” <span className="faint">— {r.author}</span></p>)}
          </div>
        )}
        {(c.faqs || []).length > 0 && (
          <div className="ws-sugg-faqs">
            <h5>Questions</h5>
            {(c.faqs || []).map((f, i) => <p key={i}><b>{f.q}</b><br />{f.a}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}

export function EarnedCities({ d, set, live, setSub }: { d: SiteContent; set: (p: any, v: any) => void; live: boolean; setSub: (s: string | null) => void }) {
  const DB: any = (window as any).VFP_DB;
  const [data, setData] = React.useState<any>(null);
  const [busy, setBusy] = React.useState("");
  const [open, setOpen] = React.useState<string | null>(null);
  const load = React.useCallback(() => {
    if (!DB || !DB.siteEarnedCities) return;
    DB.siteEarnedCities().then((r: any) => setData(r)).catch((e: any) => setData({ error: e.message || "Couldn't load suggestions.", code: e.code }));
  }, []);
  React.useEffect(() => { if (live) load(); }, [live]);
  if (!live) return null;   // the sample site: nothing of theirs to earn a page from
  if (!data) return <Group title="Towns you've earned a page in" sub="checking your finished jobs…"><p className="ws-note"><span className="ws-spin" /> Loading…</p></Group>;
  if (data.error) return <Group title="Towns you've earned a page in"><p className="ws-note">{data.code === "migration" ? data.error : "Couldn't load suggestions right now — " + data.error}</p></Group>;
  if (data.setup === false) return <Group title="Towns you've earned a page in"><p className="ws-note"><Icon name="lock" size={12} /> {data.message}</p></Group>;

  const list: Suggestion[] = data.suggestions || [];
  const cap = data.cap || { max: 8, on: 0 };
  const onNow = d.areas.cities.filter((c) => c.on).length;
  const th = data.thresholds || { jobs: 3, reviews: 2 };
  const drop = (id: string) => setData((x: any) => ({ ...x, suggestions: (x.suggestions || []).filter((y: any) => y.id !== id) }));

  const add = async (s: Suggestion) => {
    setBusy(s.id);
    try {
      const r = await DB.siteEarnedAccept(s.id);
      const page: CityPage = r.city;
      const swapSlug: string | null = r.swapSlug || null;
      const swapName = swapSlug ? (d.areas.cities.find((c) => c.slug === swapSlug) || { name: swapSlug }).name : "";
      set("areas.cities", withEarnedCity(d.areas.cities, page, swapSlug));
      drop(s.id); setOpen(null);
      setSub(page.slug);
      notify(`${s.town}'s page is in your draft${swapName ? ` and ${swapName}'s is switched off` : ""}. Publish to put it live.`, { kind: "success" });
    } catch (e: any) { notify(e.message || "Couldn't add it — try again.", { kind: "error" }); }
    setBusy("");
  };
  const dismiss = async (s: Suggestion) => {
    const n = (s.jobs || 0) + (s.reviews || 0);
    if (!(await confirmDialog({ title: `Dismiss ${s.town}?`, body: `We won't suggest a ${s.town} page again unless your work there doubles (${n} jobs and reviews now, ${2 * n} then).`, confirmLabel: "Dismiss" }))) return;
    setBusy(s.id);
    try { await DB.siteEarnedDismiss(s.id); drop(s.id); } catch (e: any) { notify(e.message || "Couldn't dismiss it — try again.", { kind: "error" }); }
    setBusy("");
  };
  const check = async () => {
    setBusy("check");
    try {
      const r = await DB.siteEarnedRefresh();
      setData(r);
      const ran = r.ran || {};
      if (!(ran.proposed || []).length && !(ran.refreshed || []).length) notify(`No new town has ${th.jobs} finished jobs or ${th.reviews} reviews yet.`);
    } catch (e: any) { notify(e.message || "Couldn't check just now — try again.", { kind: "error" }); }
    setBusy("");
  };

  const sub = list.length ? `${list.length} suggested` : `${onNow} of ${cap.max} town pages on`;
  return (
    <Group title="Towns you've earned a page in" sub={sub}>
      <p className="ws-note">When a town reaches {th.jobs} finished jobs, or {th.reviews} reviews that name it, we draft its page from your work there: how many jobs, the styles, your photos from that town and what customers there wrote. Nothing changes on your site until you add it and publish.</p>
      {list.map((s) => {
        const p = s.draft;
        const thumb = p && p.photos && p.photos[0];
        return (
          <div key={s.id} className={"ws-sugg" + (open === s.id ? " open" : "")} data-earned={s.town}>
            <div className="ws-sugg-head">
              <span className="ws-row-thumb">{thumb && thumb.src ? <img src={thumb.src} alt="" loading="lazy" /> : <Icon name="mappin" size={16} />}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <b>{earnedTitle(s)}</b>
                <span className="ws-sugg-sub">{p && p.tagline}</span>
                <span className="ws-row-sub">{[s.photos ? `${s.photos} photo${s.photos === 1 ? "" : "s"} from ${s.town}` : "", s.reviews ? `${s.reviews} review${s.reviews === 1 ? "" : "s"} name it` : "", s.suggestedAt ? "drafted " + fmtShort(s.suggestedAt) : ""].filter(Boolean).join(" · ")}</span>
              </span>
            </div>
            {s.swap && (
              <p className="ws-hint" style={{ margin: 0 }}><Icon name="bolt" size={12} /> Your site shows {cap.max} town pages at most. Adding {s.town} switches off <b>{s.swap.name}</b>{s.swap.jobs ? ` (${s.swap.jobs} job${s.swap.jobs === 1 ? "" : "s"} there)` : " (no jobs there yet)"} — you can switch it back on any time.</p>
            )}
            {open === s.id && p && <CityPreview c={p} />}
            <div className="ws-sugg-actions">
              <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || !p} onClick={() => add(s)}><Icon name="plus" size={13} />{busy === s.id ? "Adding…" : `Add ${s.town}'s page`}</button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={!p} onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? "Hide preview" : "Preview"}</button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => dismiss(s)}>Dismiss</button>
            </div>
          </div>
        );
      })}
      <div className="ws-sugg-draft">
        {!list.length && <p className="ws-note" style={{ margin: 0 }}>No town has earned a new page yet. {onNow} of {cap.max} town pages are on.</p>}
        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" className="btn btn-outline btn-sm" disabled={!!busy} onClick={check}>{busy === "check" ? <><span className="ws-spin" /> Checking…</> : <><Icon name="search" size={13} />Check my jobs now</>}</button>
          <span className="ws-hint" style={{ margin: 0 }}>{data.auto ? "We also check every morning." : ""}</span>
        </div>
      </div>
    </Group>
  );
}
